import { useState } from 'react';
import {
  AnimatedRotate,
  Icon,
  ProductThumbnail,
  Text,
  useBreakpoint
} from '@cencosud-cencommerce/paris-uikit';

import useKeyPress from '@/Application/Hooks/useKeyPress/useKeyPress';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import type { GtmGroupsData } from '@/modules/shared/utils/gtmEvents/types';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';

import type { ProductItemProps } from './ProductItem/ProductItem';
import ProductItem from './ProductItem/ProductItem';

export interface ProductListProps {
  productList?: ProductItemProps[];
  'data-testid'?: string;
  handleSetOptions: () => void;
  soldTooltip?: {
    title?: string | undefined;
    description?: string | undefined;
  };
  gtmData?: GtmGroupsData;
}
const ProductList: React.FC<ProductListProps> = ({
  'data-testid': dataTestId = 'paris-product-list',
  productList,
  handleSetOptions,
  soldTooltip,
  gtmData
}) => {
  const { isMobile } = useBreakpoint();
  const isUniqueProduct = productList && productList.length === 1;
  const [isOpen, setIsOpen] = useState(isUniqueProduct ? true : false);

  const handleOpen = () => setIsOpen((prevState) => !prevState);

  const { onKeyDown } = useKeyPress(handleOpen);

  const handleEditGtmEvent = () => {
    if (!isOpen) {
      SendShippingEvents.sendEvent({
        event: 'baseEvent',
        extraFields: {
          'event-interactive-tag':
            ShippingEventInteractiveTags.EditProductsLink,
          'event-interactive-accion': ShippingEventInteractiveActions.Click,
          'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`
        }
      });
    }
  };

  return (
    <>
      {isMobile && !isUniqueProduct && (
        <div
          className="co-flex co-justify-between co-min-h-[48px] co-gap-1 co-cursor-pointer ui-focus-2"
          tabIndex={0}
          onClick={handleOpen}
          onKeyDown={onKeyDown}
        >
          <div className="co-flex co-gap-1">
            {!isOpen && (
              <>
                {productList?.map(
                  (product: ProductItemProps, index: number) => {
                    if (index < 2) {
                      return (
                        <ProductThumbnail
                          variant="image-counter"
                          key={product.productId + index}
                          src={product.imageUrl}
                        />
                      );
                    }
                  }
                )}
                {productList && productList.length > 2 && (
                  <ProductThumbnail
                    variant="counter"
                    counterLabel={`+${String(productList.length - 2)}`}
                  />
                )}
              </>
            )}
          </div>
          <div className="co-flex co-self-center co-gap-2">
            <div className="co-flex co-text-end">
              <Text
                variant="link1"
                color="primary-500"
                weight={600}
                onClick={handleEditGtmEvent}
              >
                {isOpen ? 'Ocultar productos' : 'Editar productos'}
              </Text>
            </div>
            <AnimatedRotate isRotated={isOpen}>
              <Icon name="arrow-down" fill="primary-500" />
            </AnimatedRotate>
          </div>
        </div>
      )}
      {((isMobile && isOpen) || !isMobile) && (
        <div data-testid={dataTestId} className="co-flex co-flex-col co-gap-2">
          {productList?.map((product: ProductItemProps, index: number) => (
            <ProductItem
              key={product.productId + index}
              {...product}
              saleTagProps={product.saleTagProps}
              soldTooltip={soldTooltip}
              handleSetOptions={handleSetOptions}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default ProductList;
